import { useCallback, useEffect, useRef } from 'react';

import { useAuth } from '@/utils/auth/context';
import { AuthApi } from '@/utils/auth/types';
import { isSecurityEnabled } from '@/utils/devModeConfig';
import { envVariables } from '@/utils/env';

import { createHttpQueryError } from './queryErrorHandling';
import { headerAuthorization, headerBuildNumber } from './utils';

/**
 * Fetch-functie met dezelfde API als fetch, maar dan met het automatisch
 * gebruiken van de juiste backend API-URL en het meesturen van de JWT op alle calls als security enabled is.
 * @param query de URL, moet met een '/' beginnen. De API-URL wordt hier automatisch voorgezet.
 * @param init alle native fetch opties, zie fetch https://developer.mozilla.org/en-US/docs/Web/API/fetch
 * @param timeout de timeout in milliseconden voor de fetch call, standaard 30 seconden.
 */
export function useFetchWithAuth() {
  const { apiUrlNS, apiUrlProRail, imageBuildNumber, authProvider } = envVariables();
  const auth = useAuth();

  // We gebruiken een ref voor de access token (JWT) omdat een nieuwe token niet gelijk betekent
  // dat de callback helemaal opnieuw hoeft te worden opgebouwd.
  const authRef = useRef<AuthApi | null>(null);

  useEffect(() => {
    authRef.current = auth ?? null;
  }, [auth]);

  const apiUrl = window.location.hostname.includes('prorail') ? apiUrlProRail : apiUrlNS;

  const fetchWithAuth = useCallback(
    async (query: string, init?: RequestInit, timeout: number = 30_000) => {
      const initObj = { ...init };
      if (isSecurityEnabled() && auth) {
        // Elke fetch-call moet wel de nieuwste waarde gebruiken
        // Adapter zijn verantwoordelijk voor silent refresh indien nodig
        const token = await authRef.current?.getAccessToken();
        if (!token && query !== '/log-dump' && authProvider !== 'bff-cookie') {
          // Silent refresh is mislukt
          return Promise.reject(
            createHttpQueryError(401, 'Unauthorized', 'Token is verlopen en kan niet worden vernieuwd.'),
          );
        }

        if (authProvider === 'bff-cookie') {
          initObj.credentials = 'include';
        } else {
          // Decorate headers with bearer token (JWT)
          initObj.headers = {
            ...initObj.headers,
            ...headerAuthorization(token),
          };
        }
      }

      const correlatieId = crypto.randomUUID();
      initObj.headers = {
        ...initObj.headers,
        // Decorate headers with build image for debugging in backend
        ...headerBuildNumber(imageBuildNumber),
        // Beide types worden benodigd terwijl wij op beide ceap en azure draaien
        ...returnCorrelatieId(correlatieId, authProvider),
      };

      // Timeout of 30 seconds for all calls, browser default can be up to 300 secs.
      initObj.signal = AbortSignal.timeout(timeout);

      return fetch(apiUrl + query, initObj);
    },
    [apiUrl, imageBuildNumber],
  );

  return { fetchWithAuth };
}

export const returnCorrelatieId = (correlatieId: string, authProvider: string | undefined): Record<string, string> =>
  authProvider === 'bff-cookie' ? { 'correlation-id': correlatieId } : { correlation_id: correlatieId };
